/**
 * Logging configuration — every knob in the subsystem, resolved once.
 *
 * Nothing else in `src/logging` reads `import.meta.env`. That is the whole
 * point of this file: when the endpoint moves, or a limit needs raising, or
 * someone asks "what does this do in preview?", there is exactly one place
 * to look and exactly one place to change.
 *
 * The subsystem is inert until `VITE_LOGGING_ENDPOINT` is set. That is
 * deliberate — the web client can ship before the backend ingest endpoint
 * exists, and until it does the site makes no telemetry requests at all
 * rather than retrying against a 404.
 *
 * @module logging/config
 */

// =============================================================================
// ENVIRONMENT READS
// =============================================================================

/** `import.meta.env` is typed as an index signature returning `any`, so
 *  every read goes through here to land back in the type system. */
function readEnv(key: string): string | undefined {
  const raw: unknown = import.meta.env[key];
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

function readFlag(key: string): boolean {
  return readEnv(key) === '1';
}

/** Deploy target, which is not the same question as `import.meta.env.PROD`.
 *  A Firebase preview channel is a production build served from a
 *  `*.web.app` host, and its traffic should not be mixed into the real
 *  numbers. */
function resolveEnvironment(): 'development' | 'preview' | 'production' {
  if (import.meta.env.DEV) return 'development';
  if (typeof window !== 'undefined' && window.location.hostname.endsWith('.web.app')) {
    return 'preview';
  }
  return 'production';
}

// =============================================================================
// RESOLVED CONFIG
// =============================================================================

export interface LoggingConfig {
  /** Master switch. False means no events are built at all. */
  readonly enabled: boolean;
  /** Ingest endpoint, or null when unset — the transport stays inert. */
  readonly endpoint: string | null;
  /** `<package version>+<short git sha>`, injected by vite.config.ts. */
  readonly appVersion: string;
  readonly environment: 'development' | 'preview' | 'production';
  /** Mirror every event to the console. On by default in development. */
  readonly consoleEcho: boolean;
  /** Events per POST. Capped at 20 by the ingest contract. */
  readonly batchSize: number;
  /** Longest a completed event waits before being sent. */
  readonly flushIntervalMs: number;
  /** Buffer ceiling. Beyond this the oldest events are dropped, so a dead
   *  collector costs bounded memory rather than a growing array. */
  readonly maxQueueSize: number;
  /** Serialized bytes per POST. The ingest endpoint caps the body at 8KB;
   *  staying under it here means a rejected batch is a bug, not a routine
   *  event we would learn to ignore. */
  readonly maxBodyBytes: number;
  /** Events allowed per name before the bucket empties. */
  readonly rateLimitBurst: number;
  /** Bucket refill, per minute, per event name. */
  readonly rateLimitPerMinute: number;
  /**
   * Ceiling on any reported dwell or session duration.
   *
   * Durations already exclude hidden time, which removes the backgrounded
   * tab. This removes the other tail: a tab left *visible* and unattended
   * on a second monitor. Without a bound, one such session moves a mean by
   * an order of magnitude, and a mean that a single abandoned tab can
   * dominate is worse than one that is honestly short.
   *
   * Values arriving at exactly this number mean "at least this long,
   * truncated" — they are a floor, not a measurement, and a pile-up at the
   * boundary is the intended, visible signal of that.
   */
  readonly maxDwellMs: number;
}

const endpoint = readEnv('VITE_LOGGING_ENDPOINT') ?? null;

/**
 * Build stamp.
 *
 * `VITE_APP_VERSION` is injected by `vite.config.ts` as
 * `<package.json version>+<short git sha>`. The version alone would be
 * useless here — this repo has sat at 0.1.0 since it was created and
 * deploys continuously from `main`, so the SHA is the part that can
 * actually answer "which release regressed?". The fallback is `unknown`
 * rather than a plausible-looking constant, because a frozen version string
 * that looks real is worse than an obviously absent one.
 */
const appVersion = readEnv('VITE_APP_VERSION') ?? 'unknown';

/**
 * Mirror events to the console.
 *
 * Computed before `enabled` because it is one of the two things that can
 * make the subsystem worth running at all — see below.
 */
const consoleEcho = import.meta.env.DEV || readFlag('VITE_LOGGING_CONSOLE');

export const loggingConfig: LoggingConfig = {
  /*
   * On when there is somewhere to send events OR someone watching them.
   *
   * The `consoleEcho` half is not redundant with `DEV`. `VITE_LOGGING_CONSOLE=1`
   * exists to let someone confirm from a real browser what a deployed build
   * emits — and a deployed build with no ingest endpoint had `enabled: false`,
   * so `emit()` returned before the console transport could run and the flag
   * printed exactly nothing. The one switch for diagnosing a quiet pipeline
   * was gated behind the pipeline being non-quiet. Found by trying to use it.
   */
  enabled: !readFlag('VITE_LOGGING_DISABLED') && (endpoint !== null || consoleEcho),
  endpoint,
  appVersion,
  environment: resolveEnvironment(),
  consoleEcho,
  batchSize: 20,
  flushIntervalMs: 5_000,
  maxQueueSize: 100,
  maxBodyBytes: 8_192,
  rateLimitBurst: 20,
  rateLimitPerMinute: 30,
  maxDwellMs: 30 * 60_000,
};

/**
 * Do Not Track is deliberately not consulted.
 *
 * DNT and Global Privacy Control signal an objection to *tracking* — being
 * followed across sites or sessions by a durable identifier. This subsystem
 * has no such identifier to honour or withhold: the session id lives in
 * `sessionStorage`, dies with the tab, and is never joined to anything. The
 * same reasoning is why privacy-first analytics (Plausible, Fathom) do not
 * gate on DNT either.
 *
 * Recorded here rather than left as an absence, because "we forgot" and "we
 * considered it and this is why" look identical in code that simply omits
 * the check. If a durable identifier is ever introduced, this decision has
 * to be reopened along with the cookie-banner decision in docs/backlog.md.
 */
export const HONORS_DO_NOT_TRACK = false;
